import { ApplicationElement } from '../application_element/element.js';
import translations from './translations.js';

/**
 * Component for formatting the range between 2 dates.
 * - The date shown will always have a day and month (eg. 31.10 - 11.01).
 * - If the start and end date strings are the same, only one will be shown (to avoid for eg. 31.10 - 31.10).
 * - If the time is shown, and dates are on the same day, the to day will be shown only as time (eg. 31.10, 20:00h - 22:00h).
 *
 * @prop from - Starting date
 * @prop to - Ending date
 * @prop yearOn - Should show year? (eg. 31.10.2024 - 11.01.2025)
 * @prop weekOn - Should show day of the week? (eg. Mon 31.10 - Sat 11.01)
 * @prop timeOn - Should show time of the day? (eg. 31.10, 10:31h - 11.01, 12:35h)
 */

export default class TimeRangeV2 extends ApplicationElement {
  static properties = {
    from: { type: Date },
    to: { type: Date },
    timeOn: { type: Boolean },
    weekOn: { type: Boolean },
    yearOn: { type: Boolean },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get endsSameDay() {
    const { from, to } = this;

    return (
      from.getFullYear() === to.getFullYear() &&
      from.getMonth() === to.getMonth() &&
      from.getDate() === to.getDate()
    );
  }

  /** @internal */
  get timeFormat() {
    return `%H:%M${this.t('hour')}`;
  }

  /** @internal */
  get fromFormat() {
    let f = '%d.%m.';

    if (this.weekOn) f = '%a ' + f;
    if (this.yearOn) f += '%Y.';
    f = f.substring(0, f.length - 1); // Remove the final . on year or month (if year not present)

    if (this.timeOn) f += `, ${this.timeFormat}`;

    return f;
  }

  /** @internal */
  get toFormat() {
    return this.endsSameDay && this.timeOn ? this.timeFormat : this.fromFormat;
  }

  /** @internal */
  get fromStr() {
    return this.i18n.strftime(this.from, this.fromFormat);
  }

  /** @internal */
  get toStr() {
    return this.i18n.strftime(this.to, this.toFormat);
  }

  /** @internal */
  get showOnlyOneDate() {
    return this.fromStr === this.toStr;
  }

  render() {
    return this.showOnlyOneDate
      ? this.fromStr
      : `${this.fromStr} - ${this.toStr}`;
  }
}

TimeRangeV2.registerTagName('ef-time-range-v2');

customElements.get('ef-time-range-v2') ||
  customElements.define('ef-time-range-v2', TimeRangeV2);
